import { getTypeColor, needsDarkText } from "@/data/types";
import { cn } from "@/lib/utils";

interface TypeBadgeProps {
  typeName: string;
  variant?: "solid" | "text";
  className?: string;
  strikethrough?: boolean;
  onClick?: () => void;
  highlight?: boolean;
}

export function TypeBadge({
  typeName,
  variant = "solid",
  className,
  strikethrough = false,
  onClick,
  highlight = false,
}: TypeBadgeProps) {
  const color = getTypeColor(typeName);
  const useDarkText = needsDarkText(color);

  const baseClasses = cn(
    "inline-block px-2 sm:px-3 py-1 sm:py-1.5 rounded-md font-semibold text-xs sm:text-sm min-w-[70px] sm:min-w-[90px] text-center relative transition-all duration-300",
    onClick && "cursor-pointer hover:opacity-80",
    highlight && "scale-110 animate-pulse",
    className
  );

  const highlightStyle = highlight
    ? {
        boxShadow: "0 0 0 3px rgba(255, 255, 255, 0.8)",
      }
    : {};

  const content = (
    <>
      <span
        className={cn(
          strikethrough ? "relative z-10 px-1" : "",
          strikethrough ? (variant === "text" ? "bg-card" : "") : ""
        )}
        style={{
          ...(variant === "text" ? { color } : {}),
          ...(variant === "solid" && !strikethrough
            ? { color: useDarkText ? "#1a1a1a" : "#ffffff" }
            : {}),
          ...(strikethrough && variant === "solid"
            ? {
                backgroundColor: color,
                color: useDarkText ? "#1a1a1a" : "#ffffff",
              }
            : {}),
        }}
      >
        {typeName}
      </span>
      {strikethrough && (
        <>
          <span className="absolute inset-0 flex items-center justify-center pointer-events-none z-[5]">
            <span
              className="w-full h-[3px] rotate-[-20deg]"
              style={{
                backgroundColor: color,
              }}
            />
          </span>
          <span className="absolute inset-0 flex items-center justify-center pointer-events-none z-[5]">
            <span
              className="w-full h-[3px] rotate-[20deg]"
              style={{
                backgroundColor: color,
              }}
            />
          </span>
        </>
      )}
    </>
  );

  if (variant === "text") {
    return (
      <span
        className={cn(baseClasses, "border-2")}
        style={{ color, borderColor: color, ...highlightStyle }}
        onClick={(e) => {
          e.stopPropagation();
          onClick?.();
        }}
      >
        {content}
      </span>
    );
  }

  return (
    <span
      className={cn(baseClasses, "shadow-sm")}
      style={{
        backgroundColor: color,
        color: useDarkText ? "#1a1a1a" : "#ffffff",
        ...highlightStyle,
      }}
      onClick={(e) => {
        e.stopPropagation();
        onClick?.();
      }}
    >
      {content}
    </span>
  );
}
